import axios from "axios";
import { getToken, removeToken } from "../utils/token";
import { getTenantId } from "../utils/tenant";

// Prefer explicit env base if provided; otherwise use per-env fallbacks, then CRA proxy in dev.
const isDev = process.env.NODE_ENV === "development";
const envBase = process.env.REACT_APP_API_BASE_URL || process.env.GLOBAL_API_URL || "";
const envDev = process.env.REACT_APP_API_BASE_URL_DEV || "";
const envProd = process.env.REACT_APP_API_BASE_URL_PROD || "";

// If REACT_APP_API_BASE_URL is set, always honor it.
// If you want to force proxy mode in local dev, set REACT_APP_PREFER_PROXY_IN_DEV=1
// and ensure `proxy` is configured in package.json.
const shouldPreferProxyInDev =
	isDev &&
	typeof window !== "undefined" &&
	/^(localhost|127\.0\.0\.1)$/i.test(String(window.location.hostname || "")) &&
	String(process.env.REACT_APP_PREFER_PROXY_IN_DEV || "") === "1";

const chosen = (shouldPreferProxyInDev ? "" : (envBase || (isDev ? envDev : envProd) || ""));
const baseURL = chosen.length > 0 ? chosen : (isDev ? "/api/v1" : "");

const api = axios.create({
	baseURL,
	// Avoid client abort before slow gateway/backend (504 often = proxy/ALB timeout upstream)
	timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS) || 600000
});

if (!baseURL && typeof window !== "undefined") {
	console.warn("API base URL not set. In dev we expect CRA proxy + '/api/v1'. In prod set REACT_APP_API_BASE_URL.");
}
if (
	typeof window !== "undefined" &&
	process.env.NODE_ENV === "development" &&
	/^(localhost|127\.0\.0\.1)$/i.test(String(window.location.hostname || "")) &&
	/^https?:\/\//i.test(String(baseURL || ""))
) {
	console.warn(
		"[API] Using a full URL as REACT_APP_API_BASE_URL from localhost causes cross-origin requests (CORS). " +
			"For local dev, comment out REACT_APP_API_BASE_URL and set REACT_APP_PREFER_PROXY_IN_DEV=1 in .env, then restart."
	);
}
api.interceptors.request.use(
	(config) => {
		// Do not attach Authorization for public auth endpoints
		const path = config?.url || "";
		const isAuthPublic =
			/\/auth\/(login|register|send-verification-code|verify-code|forgot-password|reset-password)\b/.test(path);
		const isDiscoverTenant = /\/auth\/discover-tenant\b/.test(path);
		if (!isAuthPublic) {
			const token = getToken();
			if (token) {
				config.headers = config.headers ?? {};
				config.headers.Authorization = `Bearer ${token}`;
			}
		}
		// Attach tenant header if available (skip discovery call itself)
		if (!isDiscoverTenant) {
			const tenantId = getTenantId();
			if (tenantId) {
				config.headers = config.headers ?? {};
				// Respect any explicitly provided header casing
				if (!("X-Tenant-ID" in config.headers) && !("x-tenant-id" in config.headers)) {
					config.headers["X-Tenant-ID"] = tenantId;
				}
			}
		}
		return config;
	},
	(error) => Promise.reject(error)
);

api.interceptors.response.use(
	(response) => response,
	(error) => {
		const status = error?.response?.status;
		const path = error?.config?.url || "";
		const onLoginPage = (typeof window !== "undefined") && /^\/login\b/.test(window.location.pathname || "");
		// Only force logout on 401 for non-auth routes and not on login page
		if (status === 401) {
			const isAuthRoute = /\/auth\//.test(path);
			if (!isAuthRoute && !onLoginPage) {
				removeToken();
				if (typeof window !== "undefined") {
					window.location.replace("/login");
				}
			}
		}
		// Global reaction: if backend signals MFA is required, redirect to MFA setup only when not already enabled (not on login page)
		if (status === 403) {
			const errorMessage = String(error?.response?.data?.message || error?.message || "");
			if (errorMessage.includes("Multi-Factor Authentication (MFA) is required") && !onLoginPage) {
				let mfaEnabledFlag = false;
				try { mfaEnabledFlag = localStorage.getItem("mfaEnabled") === "1"; } catch {}
				if (typeof window !== "undefined") {
					const alreadyOnMfa = /\/settings\/security\/mfa-setup\b/.test(window.location.pathname);
					let redirected = false;
					try { redirected = sessionStorage.getItem("mfaRedirected") === "1"; } catch {}
					// If MFA already enabled, prefer staying and letting the UI prompt for code (no setup redirect)
					if (mfaEnabledFlag) {
						return Promise.reject(error);
					}
					if (!alreadyOnMfa && !redirected) {
						try {
							sessionStorage.setItem("mfaRedirected", "1");
							// remember where the user was heading
							const current = window.location.pathname + (window.location.search || "") + (window.location.hash || "");
							sessionStorage.setItem("mfaReturnTo", current);
						} catch {}
						window.location.href = "/settings/security/mfa-setup";
					}
				}
			}
		}
		return Promise.reject(error);
	}
);

export default api;


